import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card.js';
import { api } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';

export function DashboardPage(): JSX.Element {
  const { user, memberships } = useAuth();

  // Applications card: count whatever the caller can see (their direct-org
  // apps + cascaded descendants). Same source the Applications page uses,
  // so the number always matches what you'd see by clicking through.
  const appsQuery = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.applications.listAll(),
  });

  // Incidents card stays a placeholder until the monitoring plugin lands and
  // the SDK exposes a count endpoint. Rendered as "—" so it doesn't look
  // like a real "0" — that distinction matters for an ops dashboard.

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
        </h1>
        <p className="text-sm text-muted-foreground">
          Phase 2 in progress — auth, organisations, and identity are live. Hosting, issues, backups, and
          monitoring plugins follow next.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-4">
        <CountCard
          to="/orgs"
          label="Organisations"
          value={memberships.length}
        />
        <CountCard
          to="/p/applications"
          label="Applications"
          loading={appsQuery.isLoading}
          error={appsQuery.isError}
          {...(appsQuery.data ? { value: appsQuery.data.length } : {})}
        />
        <CountCard
          label="Open incidents"
          /* Monitoring plugin not yet wired; render as placeholder, not 0. */
          placeholder="—"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your account</CardTitle>
          <CardDescription>The principal everything you do runs as.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {user && (
            <>
              <Row label="Name" value={user.name} />
              <Row label="Email" value={user.email} />
              <Row label="User id" value={user.id} mono />
              <Row label="Email verified" value={user.emailVerified ? 'Yes' : 'No'} />
              <Row label="Member since" value={new Date(user.createdAt).toLocaleString()} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Top-row counter card. Renders a number (or a spinner / placeholder / error
 * glyph) and, if `to` is set, makes the whole card a link to the deeper view
 * — same pattern as Linear / Vercel dashboards.
 */
function CountCard({
  to,
  label,
  value,
  loading,
  error,
  placeholder,
}: {
  to?: string;
  label: string;
  value?: number;
  loading?: boolean;
  error?: boolean;
  placeholder?: string;
}): JSX.Element {
  const body = (
    <Card className={to ? 'transition-colors hover:border-primary/60' : undefined}>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : error ? (
            <span className="text-base text-destructive">error</span>
          ) : value !== undefined ? (
            value
          ) : (
            placeholder ?? '—'
          )}
        </CardTitle>
      </CardHeader>
    </Card>
  );
  if (!to) return body;
  return (
    <Link to={to} className="block">
      {body}
    </Link>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}
